import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { Console } from '@adflow/types';
import { useObjectState } from '@adflow/utils';
import {
  Button,
  FormControl as ChakraFormControl,
  FormHelperText,
  FormLabel,
  VStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Tooltip,
} from '@chakra-ui/react';
import {
  AutoComplete,
  AutoCompleteInput,
  AutoCompleteItem,
  AutoCompleteList,
} from '@choc-ui/chakra-autocomplete';
import {
  FormControl as MuiFormControl,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useAPIClient } from '../../context/apiClient';
import { useApolloClient } from '@apollo/client';
import { BrandKitBrandsGQL, CreativeHubTypes } from '@adflow/graphqlgen';

type Props = {
  isOpen: boolean;
  handleClose: () => void;
  onSubmit: (advertiser: number, selectedBrand?: CreativeHubTypes.Brand | null) => void;
  selectedAdvertiserId: number;
  disableAdvertiserSelection?: boolean;
  brandKitId?: string | null;
  currentBrandKit?: CreativeHubTypes.Brand | null;
};

type SelectAdvertiserState = {
  selectedAdvertiserId: number;
  advertisers: Console.Advertiser[];
  brands: CreativeHubTypes.Brand[];
  selectedBrandKitId: string | null;
};

type LoadingState = {
  loadingAdvertisers: boolean;
  loadingBrands: boolean;
};

const SelectAdvertiser: FC<Props> = ({
  selectedAdvertiserId,
  isOpen,
  handleClose,
  onSubmit,
  disableAdvertiserSelection = false,
  brandKitId,
}) => {
  const [state, setState] = useObjectState<SelectAdvertiserState>({
    selectedAdvertiserId,
    advertisers: [],
    brands: [],
    selectedBrandKitId: brandKitId || null,
  });

  const [loading, setLoading] = useObjectState<LoadingState>({
    loadingAdvertisers: false,
    loadingBrands: false,
  });
  const apiContext = useAPIClient();
  const apolloClient = useApolloClient();

  useEffect(() => {
    setLoading({ loadingAdvertisers: true });
    async function fetchAdvertisers() {
      const response = await apiContext.listAdvertisers();
      setState({ advertisers: response });
      setLoading({ loadingAdvertisers: false });
    }

    function wait(delay: number) {
      return new Promise((resolve) => setTimeout(resolve, delay));
    }

    let isCanceled = false;
    const retries = 10;

    function retryFetchAdvertisers(token: string, delay: number, tries: number): Promise<void> {
      const onError = (err: Error) => {
        const backoff = delay * (retries + 1 - tries);
        const triesLeft = tries - 1;
        if (triesLeft < 0) {
          throw err;
        }
        return wait(delay).then(() => {
          if (isCanceled) {
            return;
          }
          retryFetchAdvertisers(token, backoff, triesLeft);
        });
      };
      return fetchAdvertisers().catch(onError);
    }

    const token = localStorage.getItem('token');

    retryFetchAdvertisers(token as string, 2000, retries);

    return () => {
      isCanceled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!brandKitId || !state.selectedAdvertiserId || state.selectedAdvertiserId === -1) {
      setState({ brands: [] });
      return;
    }

    async function fetchBrands() {
      setLoading({ loadingBrands: true });
      try {
        const { data } = await apolloClient.query({
          query: BrandKitBrandsGQL.GET_BRANDS_BY_ADVERTISER,
          variables: { advertiserId: state.selectedAdvertiserId },
          fetchPolicy: 'network-only',
        });
        setState({ brands: data?.getBrandsByAdvertiser || [] });
      } catch (error) {
        console.error('Error fetching brands:', error);
        setState({ brands: [] });
      } finally {
        setLoading({ loadingBrands: false });
      }
    }

    fetchBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandKitId, state.selectedAdvertiserId, apolloClient]);

  const handleAdvertiserSelection = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (item: any) => {
      const parsedAdvertiserId = Number.isInteger(parseInt(item)) ? parseInt(item) : undefined;

      setState({
        selectedAdvertiserId: parsedAdvertiserId,
      });
    },
    [setState],
  );

  const handleBrandSelection = useCallback(
    (brandId: string) => {
      setState({
        selectedBrandKitId: brandId || null,
      });
    },
    [setState],
  );

  const [brandSearchText, setBrandSearchText] = useState('');

  const handleSubmit = useCallback(() => {
    if (!state.advertisers) {
      return; //TODO: handle this
    }

    const selectedBrand = brandKitId
      ? state.brands.find((b) => b.id === state.selectedBrandKitId) || null
      : undefined;
    onSubmit(state.selectedAdvertiserId, selectedBrand);
  }, [
    onSubmit,
    state.advertisers,
    state.selectedAdvertiserId,
    state.brands,
    state.selectedBrandKitId,
    brandKitId,
  ]);

  const sortedAdvertisers = useMemo(() => {
    return state.advertisers.sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
  }, [state.advertisers]);

  const sortedBrands = useMemo(() => {
    return [...state.brands].sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
  }, [state.brands]);

  const filteredBrands = useMemo(() => {
    if (!brandSearchText) return sortedBrands;
    return sortedBrands.filter((brand) =>
      brand.name.toLowerCase().includes(brandSearchText.toLowerCase()),
    );
  }, [sortedBrands, brandSearchText]);

  const selectedBrandName = useMemo(() => {
    const brand = state.brands.find((b) => b.id === state.selectedBrandKitId);
    return brand?.name || '';
  }, [state.brands, state.selectedBrandKitId]);

  const value =
    state.selectedAdvertiserId === -1 || state.selectedAdvertiserId === undefined
      ? undefined
      : state.selectedAdvertiserId.toString();

  const showBrandSelector =
    brandKitId && state.selectedAdvertiserId && state.selectedAdvertiserId !== -1;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="3xl">
      <ModalOverlay />
      <ModalContent data-test-id="select-advertiser-modal">
        <ModalHeader data-test-id="select-advertiser-title">Select an advertiser</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {/* This is done to force the component to re-render, otherwise the input
          displays the value, not label. Appears to be a bug with the library */}
          {loading.loadingAdvertisers ? (
            <Spinner />
          ) : (
            <VStack spacing={6} align="stretch" width="100%">
              <ChakraFormControl>
                <FormLabel>Advertiser</FormLabel>
                <AutoComplete
                  openOnFocus
                  isLoading={loading.loadingAdvertisers}
                  value={value}
                  multiple={false}
                  onChange={handleAdvertiserSelection}
                  restoreOnBlurIfEmpty={false}
                  closeOnBlur={true}
                  submitKeys={['Enter']}
                >
                  {disableAdvertiserSelection ? (
                    <Tooltip label="To change the advertiser, duplicate or create a new template.">
                      <AutoCompleteInput
                        onChange={(evt) => {
                          if (!evt.target.value) {
                            setState({ selectedAdvertiserId: undefined });
                          }
                        }}
                        style={{ width: '400px' }}
                        loadingIcon={<Spinner />}
                        variant="outline"
                        placeholder="Select an advertiser"
                        data-test-id="select-advertiser-input"
                        disabled={disableAdvertiserSelection}
                      />
                    </Tooltip>
                  ) : (
                    <AutoCompleteInput
                      onChange={(evt) => {
                        if (!evt.target.value) {
                          setState({ selectedAdvertiserId: undefined });
                        }
                      }}
                      style={{ width: '400px' }}
                      loadingIcon={<Spinner />}
                      variant="outline"
                      placeholder="Select an advertiser"
                      data-test-id="select-advertiser-input"
                    />
                  )}
                  <AutoCompleteList>
                    {sortedAdvertisers.map((advertiser) => (
                      <AutoCompleteItem
                        key={advertiser._id.toString()}
                        value={advertiser._id.toString()}
                        label={advertiser.name}
                        textTransform="capitalize"
                      >
                        {advertiser.name}
                      </AutoCompleteItem>
                    ))}
                  </AutoCompleteList>
                </AutoComplete>
                <FormHelperText>All new templates require an advertiser</FormHelperText>
              </ChakraFormControl>

              {showBrandSelector && (
                <ChakraFormControl>
                  <FormLabel>Brand</FormLabel>
                  {loading.loadingBrands ? (
                    <Spinner />
                  ) : (
                    <MuiFormControl fullWidth size="small">
                      <Select
                        size="small"
                        style={{ width: '400px' }}
                        value={state.selectedBrandKitId || ''}
                        onChange={(e) => handleBrandSelection(e.target.value)}
                        onClose={() => setBrandSearchText('')}
                        displayEmpty
                        data-test-id="select-brand-input"
                        renderValue={(selected) => {
                          if (!selected) {
                            return <Typography color="text.secondary">Select a brand</Typography>;
                          }
                          return <Typography>{selectedBrandName}</Typography>;
                        }}
                        MenuProps={{
                          autoFocus: false,
                          style: { zIndex: 1500 },
                          PaperProps: {
                            style: {
                              maxHeight: '300px',
                              overflowY: 'auto',
                            },
                          },
                        }}
                      >
                        <ListSubheader>
                          <TextField
                            size="small"
                            autoFocus
                            placeholder="Search brands..."
                            fullWidth
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon />
                                </InputAdornment>
                              ),
                            }}
                            onChange={(e) => setBrandSearchText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key !== 'Escape') e.stopPropagation();
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </ListSubheader>
                        {filteredBrands.map((brand) => (
                          <MenuItem key={brand.id} value={brand.id}>
                            {brand.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </MuiFormControl>
                  )}
                  <FormHelperText>Select a brand from this advertiser</FormHelperText>
                </ChakraFormControl>
              )}
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Close
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            data-test-id="confirm-advertiser-selection"
            disabled={!state.selectedAdvertiserId}
          >
            Confirm selection
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

SelectAdvertiser.displayName = 'SelectAdvertiser';

export default SelectAdvertiser;
export type { Props as SelectAdvertiserProps };
